import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Tab, Tabs, Form } from 'react-bootstrap';
import axios from 'axios';
import { API_URL, APP_URL, FILE_URL, VIDEO_THUMBNAIL } from '../../constants';
import LazyLoadImage from '../LazyLoadImage';
import Preloader from '../../user/Preloader';
import FileUploadLibrary from '../../hooks/FileUploadLibrary';

// Utility function to fetch gallery images from the API
const fetchGalleryImages = async ({
    page = 1,
    searchBy = '',
    searchValue = '',
    setGalleryImages,
    setError,
    loading,
    setLoading,
    setHasMore,
    authToken
}) => {
    if (loading) return; // Prevent duplicate requests while loading
    setLoading(true);

    try {
        const response = await axios.get(`${API_URL}/media`, {
            params: {
                limit: 30, // Fetch 30 images at a time
                currentPage: page,
                searchBy: searchBy,
                searchValue: searchValue
            },
            headers: {
                'Authorization': `Bearer ${authToken}`
            }
        });

        const fetchedImages = Object.values(response.data.data);
        const totalImages = response.data.dataCount; // Total available images
        const totalPages = Math.ceil(totalImages / 30); // Calculate total pages

        setGalleryImages(prevImages => (page === 1 ? fetchedImages : [...prevImages, ...fetchedImages]));
        setHasMore(page < totalPages); // Disable infinite scroll if we're at the last page
    } catch (err) {
        setError(err.message || 'Failed to fetch media');
    } finally {
        setLoading(false);
    }
};

function MediaModal({ show, onHide, currentInput, onSelectImage, mode = 'single', authToken }) {
    const [selectedTab, setSelectedTab] = useState('upload');
    const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
    const [galleryImages, setGalleryImages] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true); // To check if more images are available
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [copySuccess, setCopySuccess] = useState(false);

    // Handle search query change
    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const handleSearch = () => {
        setCurrentPage(1);
        setGalleryImages([]); // Clear existing images before new search
        fetchGalleryImages({
            page: 1,
            searchBy: 'mediaTitle',
            searchValue: searchText,
            setGalleryImages,
            setError,
            loading,
            setLoading,
            setHasMore,
            authToken
        });
    };

    // Handle scroll for infinite loading - removed since using Load More button
    const handleScroll = (e) => {
        // Scroll functionality removed, using Load More button instead
    };

    // Fetch images when the page changes (for infinite scroll)
    useEffect(() => {
        if (currentPage > 1) {
            fetchGalleryImages({
                page: currentPage,
                searchBy: 'mediaTitle',
                searchValue: searchText,
                setGalleryImages,
                setError,
                loading,
                setLoading,
                setHasMore,
                authToken
            });
        }
    }, [currentPage]);

    // Fetch initial images on mount
    useEffect(() => {
        if (currentPage === 1 && galleryImages.length === 0) {
            fetchGalleryImages({
                page: 1,
                searchBy: 'mediaTitle',
                searchValue: searchText,
                setGalleryImages,
                setError,
                loading,
                setLoading,
                setHasMore,
                authToken
            });
        }
    }, []);

    // Set up scroll event listener for the gallery container
    useEffect(() => {
        const galleryContainer = document.getElementById('gallery-container');
        if (galleryContainer) {
            galleryContainer.addEventListener('scroll', handleScroll);
            return () => {
                galleryContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [loading, hasMore, searchText]);

    const handleGalleryImageSelect = (image) => {
        if (mode === 'single') {
            setSelectedGalleryImage(image);
        } else {
            selectedGalleryImage === null ? setSelectedGalleryImage([image]) : setSelectedGalleryImage((prevSelected) => {
                const exists = prevSelected.find((img) => img.id === image.id);
                if (exists) {
                    return prevSelected.filter((img) => img.id !== image.id); 
                } else {
                    return [...prevSelected, image]; 
                }
            });
        }
    };

    const handleUseImage = () => {
        if (mode === 'single' && selectedGalleryImage) {
            if (selectedGalleryImage) {
                onSelectImage(`/uploads/${selectedGalleryImage.mediaLocation}/${selectedGalleryImage.mediaFilename}-full.${selectedGalleryImage.mediaExtension}`);
                onHide();
            }
        }

        if (mode === 'multiple' && Array.isArray(selectedGalleryImage) && selectedGalleryImage.length > 0) {
            const selectedFiles = selectedGalleryImage.map((img) => ({
                id: img.id,
                file_path: img.mediaExtension == 'mp4' ? `/uploads/${img.mediaLocation}/${img.mediaFilename}.${img.mediaExtension}` : `/uploads/${img.mediaLocation}/${img.mediaFilename}-full.${img.mediaExtension}`,
                ext: img.mediaExtension,
            }));
            onSelectImage(selectedFiles);
        }

        onHide();
    };

    const handleCopy = () => {
        const textToCopy = `${FILE_URL}/uploads/${selectedGalleryImage.mediaLocation}/${selectedGalleryImage.mediaFilename}-full.${selectedGalleryImage.mediaExtension}`;
        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }).catch((err) => {
            console.error("Failed to copy: ", err);
        });
    };

    return (
        <Modal show={show} onHide={onHide} size={'xl'} className='sis-modal' scrollable centered>
            <Modal.Header closeButton>
                <Modal.Title></Modal.Title>
            </Modal.Header>
            <Modal.Body className='overflow-hidden'>
                <Tabs
                    activeKey={selectedTab}
                    onSelect={(k) => setSelectedTab(k)}
                    className="mb-3 position-fixed"
                    style={{ 'margin-top': '-58px' }}
                >
                    {/* Upload Tab */}
                    <Tab eventKey="upload" title="Upload">
                        <div className="row g-3">
                            <div className="col-md-9" style={{ height: '400px', overflowY: 'scroll' }}>
                                <FileUploadLibrary onClick={handleGalleryImageSelect} authToken={authToken} />
                            </div>
                            <div className="col-md-3">
                                <div className="sidebar media-sidebar sticky-top">
                                    {selectedGalleryImage ? (
                                        <div className="selected-image">
                                            <h6 className='small'><strong>Title:</strong> {selectedGalleryImage.mediaTitle}.{selectedGalleryImage.mediaExtension}</h6>
                                            {selectedGalleryImage.mediaExtension === 'mp4' ? (
                                                <video
                                                    src={`${FILE_URL}/uploads/${selectedGalleryImage.mediaLocation}/${selectedGalleryImage.mediaFilename}.${selectedGalleryImage.mediaExtension}`}
                                                    controls
                                                    className="img-thumbnail sidebar-img"
                                                    width={100}
                                                ></video>
                                            ) : (
                                                <img
                                                    src={`${FILE_URL}/uploads/${selectedGalleryImage.mediaLocation}/${selectedGalleryImage.mediaFilename}-medium.${selectedGalleryImage.mediaExtension}`}
                                                    alt={selectedGalleryImage.mediaTitle}
                                                    className="img-thumbnail sidebar-img"
                                                    width={100}
                                                />
                                            )}
                                            <div className="input-group mt-2 mb-3">
                                                <input className="form-control" type='text' name='mediaId' value={`${FILE_URL}/uploads/${selectedGalleryImage.mediaLocation}/${selectedGalleryImage.mediaFilename}-thumbnail.${selectedGalleryImage.mediaExtension}`} readOnly />
                                                <span className="input-group-text" id="basic-addon1">
                                                    <i
                                                        className="bi bi-copy"
                                                        onClick={handleCopy}
                                                        style={{ cursor: 'pointer' }}
                                                    ></i>
                                                </span>
                                                {copySuccess && <div className="alert alert-success position-absolute end-0 py-0 px-2 rounded-0 badge" role="alert" style={{ top: '37px' }}>Copied!</div>}
                                            </div>
                                        </div>
                                    ) : 'No image selected'}
                                </div>
                            </div>
                        </div>
                    </Tab>

                    {/* Media Library Tab */}
                    <Tab eventKey="mediaLibrary" title="Media Library">
                        <div className="row g-3">
                            <div
                                id="gallery-container" className="col-md-9"
                                style={{ height: '400px', overflowY: 'scroll' }}>

                                {/* {error && <div className="alert alert-danger">{error}</div>} */}
                                {galleryImages.length === 0 ? (
                                    loading ? <Preloader /> : <p className="text-center text-muted py-5">No images available in the gallery.</p>
                                ) : (
                                    <div className="row row-cols-3 row-cols-lg-6 row-cols-md-5 g-2 media_loader_gallery">
                                        {galleryImages.map((image) => (
                                            <div key={image.id} className="col position-relative">
                                                <LazyLoadImage
                                                    imgSrc={image.mediaExtension === 'mp4' ? VIDEO_THUMBNAIL : `${FILE_URL}/uploads/${image.mediaLocation}/${image.mediaFilename}-medium.${image.mediaExtension}`}
                                                    alt={image.mediaTitle}
                                                    className="img-thumbnail"
                                                    width={100}
                                                    onClick={() => handleGalleryImageSelect(image)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                {mode === 'single' && selectedGalleryImage?.id === image.id && (
                                                    <Form.Check
                                                        type="checkbox"
                                                        className="form-check position-absolute top-0 end-0 p-2"
                                                        checked
                                                        onChange={() => handleGalleryImageSelect(image)}
                                                    />
                                                )}

                                                {mode === 'multiple' && (
                                                    <Form.Check
                                                        type="checkbox"
                                                        className="form-check position-absolute top-0 end-0 p-2"
                                                        checked={selectedGalleryImage?.some((img) => img.id === image.id)}
                                                        onChange={() => handleGalleryImageSelect(image)}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="col-md-3">
                                <div className="sidebar media-sidebar sticky-top">
                                    <div className="input-group mt-2 mb-3">
                                        <input
                                            className="form-control form-control-sm"
                                            type="text"
                                            placeholder='Enter for search...'
                                            value={searchText}
                                            onChange={handleSearchChange}
                                        />
                                        <Button className='btn input-group-button' variant="secondary" onClick={handleSearch}>Search</Button>
                                    </div>

                                    {selectedGalleryImage ? (
                                        <div className="selected-image">
                                            <h6 className='small'><strong>Title:</strong> {selectedGalleryImage.mediaTitle}.{selectedGalleryImage.mediaExtension}</h6>
                                            {selectedGalleryImage.mediaExtension === 'mp4' ? (
                                                <video
                                                    src={`${FILE_URL}/uploads/${selectedGalleryImage.mediaLocation}/${selectedGalleryImage.mediaFilename}.${selectedGalleryImage.mediaExtension}`}
                                                    controls
                                                    className="img-thumbnail sidebar-img"
                                                    width={100}
                                                ></video>
                                            ) : (
                                                <img
                                                    src={`${FILE_URL}/uploads/${selectedGalleryImage.mediaLocation}/${selectedGalleryImage.mediaFilename}-medium.${selectedGalleryImage.mediaExtension}`}
                                                    alt={selectedGalleryImage.mediaTitle}
                                                    className="img-thumbnail sidebar-img"
                                                    width={100}
                                                />
                                            )}
                                            <div className="input-group mt-2 mb-3">
                                                <input className="form-control" type='text' name='mediaId' value={`${FILE_URL}/uploads/${selectedGalleryImage.mediaLocation}/${selectedGalleryImage.mediaFilename}-thumbnail.${selectedGalleryImage.mediaExtension}`} readOnly />
                                                <span className="input-group-text" id="basic-addon1">
                                                    <i
                                                        className="bi bi-copy"
                                                        onClick={handleCopy}
                                                        style={{ cursor: 'pointer' }}
                                                    ></i>
                                                </span>
                                                {copySuccess && <div className="alert alert-success position-absolute end-0 py-0 px-2 rounded-0 badge" role="alert" style={{ top: '37px' }}>Copied!</div>}
                                            </div>
                                        </div>
                                    ) : 'No image selected'}
                                </div>
                            </div>
                            <div className='col-md-9 text-center mt-3'>
                                {galleryImages.length > 0 && hasMore && (
                                    <Button 
                                        variant="primary" 
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                        disabled={loading}
                                        className='mb-3'
                                    >
                                        {loading ? 'Loading...' : 'Load More Images'}
                                    </Button>
                                )}
                                {galleryImages.length > 0 && !hasMore && (
                                    <p className="text-muted mb-3">No more images found.</p>
                                )}
                                {loading && galleryImages.length > 0 && <Preloader />}
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
                <Button variant="primary" onClick={handleUseImage} disabled={!selectedGalleryImage}>
                    Use Image
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default MediaModal;
